import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment';
import { toast } from 'react-toastify';

import PageTitle from '../../common/PageTitle';
import SpinnerBlock from '../../common/SpinnerBlock';
import Button from '../../common/Button';
import Pagination from '../../common/Pagination';

import { fetchGetProductReviews, fetchUpdateProductReview } from '../../../actions/items';

import { getProductImageUrl, parseError } from '../../../helpers';

import { REVIEWS_COUNT_ON_PAGE } from '../../../config';

const ReviewsTabsWrapper = styled('div')`
  .nav-tabs-reviews {
    position: absolute;
    top: -38px;
  }

  .nav-tabs-reviews .nav-item.show .nav-link,
  .nav-tabs-reviews .nav-link.active {
    color: #7e57c2;
    background-color: #fff;
    //box-shadow: 0 1px 4px 0 rgba(0,0,0,.1);
  }

  .nav-tabs-reviews .nav-link-after {
    content: '';

    left: 0px;
    width: 100%;
    height: 5px;
    background-color: #fff;
    border: none;
  }

  .nav-tabs-reviews .nav-link-after {
    border-top: 1px solid #edf0f2;

    margin-top: -1px;
  }

  .nav-tabs-reviews .nav-link-after.active {
    border-top: 0px;
    margin-top: 0px;
  }

  .nav-tabs-reviews .nav-link.active .badge {
    background-color: #7e57c2;
    color: #fff;
  }

  .nav-tabs-reviews .nav-link .badge {
    background-color: #dfe4e6;
    color: #828489;
    margin-left: 10px;
    font-weight: 600;
  }

  .nav-tabs-reviews .nav-link .badge {
  }

  .nav-tabs-reviews .nav-item {
    margin-right: 4px;
  }

  .nav-tabs-reviews .nav-link {
    border: 1px solid transparent;
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
    background-color: #fbfcfd;
  }

  .nav-tabs-reviews .nav-link.active {
    border: 1px solid transparent;
  }

  .nav-tabs-reviews .nav-link:hover {
    background-color: #fff;
  }

  .nav-tabs-reviews > li > a {
    color: #7a7c82;
    font-weight: 600;
  }

  .nav-link {
    display: block;
    padding: 0.5rem 1rem;
    cursor: pointer;
  }

  .nav-link .nav-link-icon {
    padding-top: 1px;
    margin-right: 3px;
  }

  .nav-link i {
    font-size: 1rem;
    margin-right: 3px;
    margin-top: 4px;
  }

  @media (max-width: 1205px) {
    .tab-title {
      display: none;
    }
  }

  @media (max-width: 520px) {
    .nav-link {
      padding-right: 5px;
      padding-left: 5px;
      .badge {
        display: none;
      }
    }
  }
`;

const TableWrapper = styled('div')`
  position: relative;

  .review-product-name {
    font-weight: bold;
  }

  .review-data-wrapper {
    .review-data {
      display: flex;
      align-items: center;
      > div {
        margin-right: 5px;
        &:last-child {
          margin-right: 0;
        }
      }
    }
    span:not(.more-link) {
      font-weight: bold;
    }
    .more-link {
      color: #7e57c2;
      cursor: pointer;
      margin-left: 5px;
      text-decoration: underline;
    }
  }

  .review-buttons {
    display: flex;
    min-width: 250px;
  }
`;

const getStars = (score) => {
  const count = score || 0;
  const resultArr = [];
  for (let i = 0; i < 5; i++) {
    resultArr.push(<i className={i > count ? 'fe-star' : 'fe-star-on'} />);
  }
  return resultArr;
};

const ReviewContent = (props) => {
  const { content } = props;

  const [moreIsOpen, setMoreIsOpen] = useState(false);
  let showMore = false;
  let formattedContent = content;

  if (content.length > 200 && !moreIsOpen) {
    showMore = true;
    formattedContent = content.substr(0, 200) + '...';
  }

  return (
    <>
      {formattedContent}
      {showMore ? (
        <span className="more-link" onClick={() => setMoreIsOpen(true)}>
          Читать полностью
        </span>
      ) : null}
    </>
  );
};

const ReviewsPage = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('new');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    (async function () {
      await getReviews().then((res) => {
        setPage(0);
        setTotalItems(res.recordsTotal);
      });
    })();
  }, [activeTab]);

  const getReviews = async () => {
    setLoading(true);
    return await dispatch(
      fetchGetProductReviews({
        size: REVIEWS_COUNT_ON_PAGE,
        page,
        status: activeTab === 'new' ? 'NEW' : null,
      }),
    )
      .then((res) => {
        setReviews(res.reviews);
        return res;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePublish = async (review) => {
    setPublishLoading(true);
    await dispatch(
      fetchUpdateProductReview({
        ...review,
        status: 'CONFIRMED',
      }),
    )
      .then(() => {
        toast.info('Отзыв успешно опубликован');
      })
      .catch((err) => {
        toast.error(parseError(err));
      });
    setPublishLoading(false);
    getReviews();
  };

  const handleDelete = async (review) => {
    setDeleteLoading(true);
    await dispatch(
      fetchUpdateProductReview({
        ...review,
        status: 'DELETED',
      }),
    )
      .then(() => {
        toast.info('Отзыв успешно удален');
      })
      .catch((err) => {
        toast.error(parseError(err));
      });
    setDeleteLoading(false);
    getReviews();
  };

  return (
    <>
      <PageTitle title="Отзывы к товарам" />

      <div className="row">
        <div className="col-12">
          <div className="card" style={{ marginTop: '38px' }}>
            <ReviewsTabsWrapper>
              <ul className="nav nav-tabs-reviews">
                <li className="nav-item">
                  <div
                    data-toggle="tab"
                    aria-expanded="true"
                    className={`nav-link ${activeTab === 'new' ? 'active' : ''}`}
                    onClick={() => setActiveTab('new')}
                  >
                    <div className="d-flex flex-row">
                      <div className="nav-link-icon">
                        <i className="fe-bell" />
                      </div>
                      <div className="tab-title">Новые</div>
                    </div>
                  </div>
                  <div className={`nav-link-after ${activeTab === 'new' ? 'active' : ''}`} />
                </li>

                <li className="nav-item">
                  <div
                    data-toggle="tab"
                    aria-expanded="true"
                    className={`nav-link ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveTab('all')}
                  >
                    <div className="d-flex flex-row">
                      <div className="nav-link-icon">
                        <i className="fe-list" />
                      </div>
                      <div className="tab-title">Все отзывы</div>
                    </div>
                  </div>
                  <div className={`nav-link-after ${activeTab === 'all' ? 'active' : ''}`} />
                </li>
              </ul>
            </ReviewsTabsWrapper>
            <div className="card-body">
              <TableWrapper>
                <SpinnerBlock show={loading} />
                <div className="bootstrap-table">
                  <div className="fixed-table-container">
                    <div className="fixed-table-body">
                      <table className="table table-centered">
                        <thead className="thead-light">
                          <tr>
                            <th />
                            <th>Наименование</th>
                            <th>Отзыв</th>
                            <th>Действия</th>
                          </tr>
                        </thead>

                        <tbody>
                          {reviews.map((r) => (
                            <tr key={r.id}>
                              <td>
                                <div
                                  className="image-block"
                                  style={{
                                    backgroundImage:
                                      r.product.images && r.product.images.length
                                        ? `url(${getProductImageUrl(
                                            { defaultImage: r.product.images[0].url },
                                            100,
                                          )})`
                                        : '',
                                  }}
                                />
                              </td>
                              <td className="review-product-name">{r.product.shortName}</td>
                              <td className="review-data-wrapper">
                                <div className="review-data">
                                  <div>
                                    <span>Имя:</span>
                                    {` ${r.customerName},`}
                                  </div>
                                  <div>
                                    <span>Дата:</span>
                                    {` ${moment(r.createTs).format('DD.MM.YYYY hh:mm')},`}
                                  </div>
                                  <div>
                                    <span>Рейтинг:</span>
                                    {` `}
                                    {getStars(r.score)}
                                  </div>
                                </div>
                                <div>
                                  <span>Отзыв:</span> <ReviewContent content={r.content} />
                                </div>
                              </td>
                              <td>
                                <div className="review-buttons">
                                  {r.status === 'NEW' ? (
                                    <Button
                                      className="mr-2 ml-auto"
                                      text="Опубликовать"
                                      loading={publishLoading}
                                      disabled={publishLoading || deleteLoading}
                                      onClick={() => handlePublish(r)}
                                    />
                                  ) : null}
                                  <Button
                                    className={r.status !== 'NEW' ? 'ml-auto' : ''}
                                    classType="btn-danger"
                                    icon="fe-trash-2"
                                    loading={deleteLoading}
                                    disabled={deleteLoading || publishLoading}
                                    onClick={() => handleDelete(r)}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </TableWrapper>
              <Pagination
                total={totalItems}
                currentPage={Number(page)}
                onPageSelect={(pageIndex) => setPage(pageIndex)}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReviewsPage;
